import React, { useState } from 'react';
import { Facebook, Instagram, Twitter, Linkedin, Mail, Phone, MapPin, Send, ArrowUpRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

export const Footer: React.FC = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
    }
  };

  const supportLinks = [
    { name: 'Help Center', path: '/help' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Cookie Policy', path: '/privacy' }
  ];

  const platformLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Contact', path: '/contact' }
  ];

  return (
    <footer className="bg-secondary text-white pt-24 pb-12 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-1/4 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px] -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
          
          {/* Brand Column */}
          <div className="lg:col-span-4 space-y-8">
             <div className="flex items-center gap-2">
                <Link to="/" className="inline-block">
                   <img 
                      src="https://09da45aa8861e9e09f066d7bf0bbd9f0.cdn.bubble.io/f1765291597180x898425436812830000/Final-Logo-horizontal.jpg" 
                      alt="Men's Health Clinic Africa"
                      className="h-20 w-auto bg-white rounded-xl p-3 object-contain"
                   />
                </Link>
            </div>
            <p className="text-gray-400 leading-relaxed pr-6 text-sm">
              Empowering men to take control of their health through accessible, modern, and confidential healthcare solutions across Africa.
            </p>
            
            <div className="space-y-4 pt-2">
                <p className="text-xs font-bold text-white uppercase tracking-wider">Subscribe to our newsletter</p>
                {subscribed ? (
                  <div className="flex items-center gap-2 text-green-400 bg-green-500/10 p-3 rounded-xl border border-green-500/20 animate-fade-in-up">
                    <CheckCircle2 size={18} />
                    <span className="text-sm font-medium">Thanks for subscribing!</span>
                  </div>
                ) : (
                  <form onSubmit={handleSubscribe} className="flex items-center bg-white/5 border border-white/10 rounded-full p-1 pl-4 focus-within:border-primary/50 focus-within:bg-white/10 transition-all max-w-sm">
                      <input 
                          type="email" 
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Email address" 
                          required
                          className="bg-transparent border-none text-white text-sm w-full focus:outline-none placeholder:text-gray-600 h-10"
                      />
                      <button type="submit" className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white hover:bg-blue-600 transition-colors shadow-lg shadow-primary/20">
                          <Send size={16} className="-ml-0.5 mt-0.5" />
                      </button>
                  </form>
                )}
            </div>
          </div>

          {/* Links Column 1 */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className="font-bold text-base mb-6 text-white">Platform</h4>
            <ul className="space-y-3 text-gray-400 text-sm">
              {platformLinks.map((item) => (
                <li key={item.name}>
                    <Link to={item.path} className="hover:text-primary transition-colors inline-flex items-center gap-1 group py-1">
                        {item.name}
                        <ArrowUpRight size={10} className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Links Column 2 */}
          <div className="lg:col-span-2">
            <h4 className="font-bold text-base mb-6 text-white">Support</h4>
             <ul className="space-y-3 text-gray-400 text-sm">
              {supportLinks.map((item) => (
                <li key={item.name}>
                    {item.path.startsWith('/') ? (
                        <Link to={item.path} className="hover:text-primary transition-colors inline-block hover:translate-x-1 transition-transform py-1">
                            {item.name}
                        </Link>
                    ) : (
                        <a href={item.path} className="hover:text-primary transition-colors inline-block hover:translate-x-1 transition-transform py-1">
                            {item.name}
                        </a>
                    )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div className="lg:col-span-3">
            <h4 className="font-bold text-base mb-6 text-white">Contact</h4>
            <ul className="space-y-5 text-gray-400 text-sm">
              <li className="flex items-start gap-3 group">
                <MapPin size={18} className="text-primary mt-0.5 flex-shrink-0" />
                <span>281a Herbert Chitepo Avenue,<br/>Harare</span>
              </li>
              <li>
                <a href="tel:+263776621188" className="flex items-center gap-3 group hover:text-primary transition-colors">
                  <Phone size={18} className="text-primary flex-shrink-0" />
                  <span>+263 776 621 188</span>
                </a>
              </li>
               <li>
                <a href="mailto:support@menshealthclinic.africa" className="flex items-center gap-3 group hover:text-primary transition-colors">
                  <Mail size={18} className="text-primary flex-shrink-0" />
                  <span>support@menshealthclinic.africa</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-gray-600">© 2025 Men's Health Clinic Africa. All Rights Reserved.</p>
          
          <div className="flex gap-3">
             {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => (
                <button key={i} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300 cursor-default">
                    <Icon size={16} />
                </button>
             ))}
          </div>
        </div>
      </div>
    </footer>
  );
};